import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
    collection,
    query, onSnapshot, orderBy
} from 'firebase/firestore';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
    Stack, CircularProgress, Autocomplete, Alert
} from '@mui/material';

const formatPlaka = (value) => {
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    let result = '';
    const numbers = cleaned.match(/\d+/g) || [];
    const letters = cleaned.match(/[A-Z]+/g) || [];

    if (numbers.length > 0) result += numbers[0].substring(0, 2);

    if (letters.length > 0) {
        if (result.length > 0) result += ' ';
        result += letters[0].substring(0, 3);
    }

    if (numbers.length > 1) {
        if (result.length > 0 && !result.endsWith(' ')) result += ' ';
        result += numbers[1].substring(0, 4);
    }
    return result;
};

const VehicleFormModal = ({ isOpen, onClose, onAddVehicle, onUpdateVehicle, vehicleToEdit, preselectedCustomer }) => {
    const [marka, setMarka] = useState('');
    const [model, setModel] = useState('');
    const [yil, setYil] = useState('');
    const [renk, setRenk] = useState('');
    const [plaka, setPlaka] = useState('');

    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [brands, setBrands] = useState([]);

    const [isSaving, setIsSaving] = useState(false);
    const [hata, setHata] = useState('');
    const isEditing = Boolean(vehicleToEdit);

    useEffect(() => {
        const q = query(collection(db, "customers"), orderBy("adSoyad", "asc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setCustomers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const q = query(collection(db, "vehicleBrands"), orderBy("name", "asc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setBrands(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (isOpen) {
            setHata('');
            if (isEditing && vehicleToEdit) {
                setMarka(vehicleToEdit.marka || '');
                setModel(vehicleToEdit.model || '');
                setYil(vehicleToEdit.yil || '');
                setRenk(vehicleToEdit.renk || '');
                setPlaka(vehicleToEdit.plaka || '');
            } else {
                setMarka(''); setModel(''); setYil(''); setRenk(''); setPlaka('');

                if (preselectedCustomer) {
                    setSelectedCustomer(preselectedCustomer);
                } else {
                    setSelectedCustomer(null);
                }
            }
        }
    }, [isOpen, vehicleToEdit, isEditing, preselectedCustomer]);

    // Düzenlemede araç sahibi SEÇİLMİYOR (alan kilitli) ama ekranda görünmeli.
    // Müşteri listesi asenkron geldiği için ayrı efekt: liste dolduğunda eşleştirilir.
    // Sahibi burada yalnız GÖSTERİLİR — kaydetme payload'ına girmez (aşağıya bak).
    useEffect(() => {
        if (!isOpen || !isEditing || !vehicleToEdit?.musteriId) return;
        const sahip = customers.find(c => c.id === vehicleToEdit.musteriId);
        setSelectedCustomer(sahip || (vehicleToEdit.musteriAdSoyad
            ? { id: vehicleToEdit.musteriId, adSoyad: vehicleToEdit.musteriAdSoyad }
            : null));
    }, [isOpen, isEditing, vehicleToEdit, customers]);

    const handleSubmit = async () => {
        setHata('');
        if (!plaka || !marka || !model) {
            setHata('Lütfen zorunlu alanları (Plaka, Marka, Model) doldurunuz.');
            return;
        }

        if (!isEditing && !selectedCustomer && !preselectedCustomer) {
            setHata('Lütfen bir müşteri seçiniz.');
            return;
        }

        setIsSaving(true);
        try {
            if (isEditing) {
                // KRİTİK: düzenlemede `musteriId` payload'a KONMAZ.
                // Müşteri seçici düzenlemede kilitli olduğu için `selectedCustomer`
                // eskiden hep null kalıyor, `musteriId: undefined` üretiliyordu;
                // `ignoreUndefinedProperties` kapalı olduğundan `batch.update` senkron
                // olarak patlıyor ve hata yutulduğu için kayıt SESSİZCE düşüyordu.
                // Alanı hiç göndermemek, mevcut sahibi de kazara ezmemeyi garanti eder.
                await onUpdateVehicle(vehicleToEdit.id, {
                    marka, model, yil, renk, plaka: plaka.toUpperCase()
                });
            } else {
                const musteri = preselectedCustomer || selectedCustomer;
                await onAddVehicle({
                    marka, model, yil, renk, plaka: plaka.toUpperCase(),
                    musteriId: musteri.id,
                    // Araç listesi "Araç Sahibi" sütununu bu denormalize alandan okur;
                    // eskiden yazılmadığı için bu ekrandan eklenen araçlarda boş kalıyordu.
                    musteriAdSoyad: musteri.adSoyad || ''
                });
            }
            onClose();
        } catch (error) {
            console.error("Araç kaydedilirken hata:", error);
            setHata(error?.message || 'Araç kaydedilemedi. Lütfen tekrar deneyin.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEditing ? 'Aracı Düzenle' : 'Yeni Araç Ekle'}</DialogTitle>
            <DialogContent>
                <Stack component="form" spacing={2} sx={{ mt: 2 }}>

                    {hata && <Alert severity="error" onClose={() => setHata('')}>{hata}</Alert>}

                    <Autocomplete
                        value={selectedCustomer}
                        onChange={(event, newValue) => {
                            setSelectedCustomer(newValue);
                        }}
                        options={customers}
                        getOptionLabel={(option) => option.adSoyad || ''}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={isEditing ? 'Araç Sahibi' : 'Müşteri Seçin *'}
                                variant="outlined"
                                helperText={isEditing ? 'Araç sahibi bu ekrandan değiştirilemez (kayıt sırasında korunur).' : ''}
                            />
                        )}
                        disabled={isEditing || Boolean(preselectedCustomer)}
                    />

                    <TextField
                        label="Plaka *"
                        value={plaka}
                        onChange={(e) => setPlaka(formatPlaka(e.target.value))}
                        required
                        fullWidth
                        inputProps={{ maxLength: 10 }}
                    />

                    <Autocomplete
                        freeSolo
                        value={marka}
                        onChange={(event, newValue) => {
                            setMarka(newValue);
                            setModel('');
                        }}
                        onInputChange={(event, newInputValue) => {
                            setMarka(newInputValue);
                        }}
                        options={brands.map(b => b.name)}
                        renderInput={(params) => <TextField {...params} label="Marka *" required />}
                    />

                    <Autocomplete
                        freeSolo
                        value={model}
                        onChange={(event, newValue) => setModel(newValue)}
                        onInputChange={(event, newInputValue) => setModel(newInputValue)}
                        options={brands.find(b => b.name === marka)?.models || []}
                        disabled={!marka}
                        renderInput={(params) => <TextField {...params} label="Model *" required />}
                    />

                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Yıl"
                            value={yil}
                            onChange={(e) => setYil(e.target.value)}
                            type="number"
                            fullWidth
                        />
                        <TextField
                            label="Renk"
                            value={renk}
                            onChange={(e) => setRenk(e.target.value)}
                            fullWidth
                        />
                    </Stack>

                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px' }}>
                <Button onClick={onClose} disabled={isSaving}>İptal</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={isSaving}>
                    {isSaving ? <CircularProgress size={24} /> : (isEditing ? 'Güncelle' : 'Kaydet')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default VehicleFormModal;